import { Button, Card, Group, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { Mail, MailCheck, Printer } from "lucide-react";
import { useStartPrinting } from "@/hooks/use-start-printing";

const STEPS = [
  "Press the button — PaperCut emails you a personal setup link.",
  "Follow the link to add the printers on your computer or phone.",
  "Print from any app, then release the job at a printer with your access card.",
];

/**
 * Print onboarding for a member who has never set up a print account: what
 * it is, three steps, and the button that has PaperCut email the setup link.
 * The Print page's empty state, also the `print.start` guide block (#436) —
 * `onCompleted` fires when the email went out.
 */
export function StartPrintingCard({ onCompleted }: { onCompleted?: () => void }) {
  const startPrinting = useStartPrinting(onCompleted);
  return (
    <Card padding="xl" withBorder maw={560} w="100%">
      <Stack align="center" gap="md" ta="center">
        <ThemeIcon size={72} radius="xl" variant="light">
          <Printer size={36} />
        </ThemeIcon>
        <Title order={2}>Start printing</Title>
        <Text c="dimmed" fz="sm" maw={420}>
          Your access card already works at the printers here. Set up a print
          account to print straight from your own laptop or phone.
        </Text>
        <Stack gap="sm" mt="xs" w="100%" maw={420}>
          {STEPS.map((step, i) => (
            <Group key={step} gap="sm" wrap="nowrap" align="flex-start" ta="left">
              <ThemeIcon size={24} radius="xl" variant="light">
                <Text fz="xs" fw={700}>{i + 1}</Text>
              </ThemeIcon>
              <Text fz="sm">{step}</Text>
            </Group>
          ))}
        </Stack>
        {startPrinting.isSuccess ? (
          <Stack gap={4} mt="md" align="center">
            <Group gap="xs">
              <MailCheck size={18} />
              <Text fw={600}>Setup email sent</Text>
            </Group>
            <Text fz="sm" c="dimmed">Check your inbox — and the spam folder, just in case.</Text>
            <Button
              variant="subtle"
              size="xs"
              loading={startPrinting.isPending}
              onClick={() => startPrinting.mutate()}
            >
              Send it again
            </Button>
          </Stack>
        ) : (
          <Button
            size="md"
            mt="md"
            leftSection={<Mail size={18} />}
            loading={startPrinting.isPending}
            onClick={() => startPrinting.mutate()}
          >
            Email me the setup link
          </Button>
        )}
      </Stack>
    </Card>
  );
}
